import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type Paginated } from '@/shared/api/client';
import type { Customer, CustomerContact } from '@/shared/api/types';

export interface CustomerDetail extends Customer {
  contacts: CustomerContact[];
  objects: { id: string; name: string; active: boolean }[];
}

export function useCustomers(q: string) {
  return useQuery({
    queryKey: ['customers', q],
    queryFn: () =>
      api<Paginated<Customer>>(`/api/customers?limit=100${q ? `&q=${encodeURIComponent(q)}` : ''}`),
  });
}

export function useCustomer(id: string | null) {
  return useQuery({
    queryKey: ['customer', id],
    queryFn: () => api<CustomerDetail>(`/api/customers/${id}`),
    enabled: !!id,
  });
}

export interface CustomerInput {
  companyName: string;
  billingAddress?: string;
  email?: string;
  phone?: string;
  notes?: string;
}

export function useCreateCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CustomerInput) =>
      api<Customer>('/api/customers', { method: 'POST', body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['customers'] }),
  });
}

export function useUpdateCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<CustomerInput> }) =>
      api<Customer>(`/api/customers/${id}`, { method: 'PATCH', body: input }),
    onSuccess: (_d, { id }) => {
      qc.invalidateQueries({ queryKey: ['customers'] });
      qc.invalidateQueries({ queryKey: ['customer', id] });
    },
  });
}

export function useDeleteCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<{ id: string }>(`/api/customers/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['customers'] }),
  });
}

export interface ContactInput {
  name: string;
  email?: string;
  phone?: string;
  approvalAuthorized?: boolean;
}

export function useAddContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, input }: { customerId: string; input: ContactInput }) =>
      api<CustomerContact>(`/api/customers/${customerId}/contacts`, { method: 'POST', body: input }),
    onSuccess: (_d, { customerId }) => qc.invalidateQueries({ queryKey: ['customer', customerId] }),
  });
}

export function useDeleteContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, contactId }: { customerId: string; contactId: string }) =>
      api<{ id: string }>(`/api/customers/${customerId}/contacts/${contactId}`, { method: 'DELETE' }),
    onSuccess: (_d, { customerId }) => qc.invalidateQueries({ queryKey: ['customer', customerId] }),
  });
}
